// `time-bucket` pipeline verb — bucket rows by time at a `:period`
// granularity and aggregate each bucket. Two forms (PHASE_4_PREP.md
// D11.2), selected by the substrate's per-cell dispatch, NOT by a
// surface flag:
//
//   Plain-timestamp (row-level):
//     (time-bucket ts :period week :group-by [region]
//                     :agg [(t (mean temp))])
//
//   Per-cell (TS-ref / nested-dataset columns — single or vector):
//     (time-bucket [oilProd gasProd] :period day
//                  :window ["2026-01-01" "2026-04-01"]
//                  :agg [min max mean])
//
// When `params[0]` names one or more `nested-dataset` columns the
// dispatch wrapper lifts this verb per cell (multi-axis, D11.1):
// each cell's inner Dataset has the TS-ref shape
// `{timestamp, value, isString, unit}`; we bucket its `timestamp`
// and aggregate its `value` with the bare `:agg` symbols, emitting a
// nested-dataset column (same outer name) whose inner schema is
// `{bucket, <col>-<agg>…}`. With `:window` every cell — across every
// axis — emits the SAME deterministic grid (gap buckets null-filled),
// so multi-column `unnest` (D11.3) can positionally zip them. The
// per-cell form rejects `:group-by` and the pair `:agg` form; the
// plain form rejects the vector positional and bare `:agg`.
//
// Inside the pair `:agg`, calls dispatch through
// `aggContextScope.registry` (same as `aggregate` — see
// `agg-funcs.ts`).

import { z } from 'zod';

import { NO_POS } from '../../../ast.ts';
import { ShapeError, TypeMismatchError } from '../../../errors.ts';
import { astShape, opCallPos } from '../../../normalize.ts';
import { type ColumnRefInArg, type PropagateSchemaCtx, pipelineVerbs, type SpecialVerbCtx } from '../../../op-meta.ts';
import { paramPath } from '../../../path.ts';
import { groupRowsBy, materializedToRows } from '../../../runtime/arquero.ts';
import {
  type ColumnType,
  nestedDatasetCol,
  type Schema,
  schemaFromEntries,
  timestampCol,
  unknownCol,
} from '../../../schema/types.ts';
import { type Cell, MaterializedDataset } from '../../../value.ts';
import {
  AggPairSchema,
  aggContextScope,
  BARE_AGG_SYMBOLS,
  compileAggPair,
  compileBareAgg,
  isBareAggSymbol,
} from '../agg-funcs.ts';
import { bucketTimestamp, type Period, Periods, periodGrid, toDateValue } from '../date-utils.ts';

// biome-ignore lint/style/useNamingConvention: $-prefixed engine reserved
const ColSymbol = z.object({ $sym: z.string().min(1) });

// First positional: a single column OR a non-empty vector of columns.
// The vector is valid ONLY when every entry is a TS-ref
// (`nested-dataset`) column — enforced at apply, since column types
// are not known at parse time.
const ColsArg = z.union([ColSymbol, z.array(ColSymbol).min(1)]);

// One `:agg` entry — a bare agg symbol (`min`/`max`/… ⇒ `{$sym}`,
// per-cell form) OR an `(out (agg col))` pair (plain form). The two
// forms are mutually exclusive per call; the active dispatch form
// enforces it.
const AggEntry = z.union([ColSymbol, AggPairSchema]);

type ColSym = z.infer<typeof ColSymbol>;
type AggPair = z.infer<typeof AggPairSchema>;
type AggEntryT = ColSym | AggPair;

const isColSym = (e: AggEntryT): e is ColSym => '$sym' in e;

/** Column names from the first positional (single or vector). */
const colsOf = (first: ColSym | readonly ColSym[]): string[] =>
  Array.isArray(first) ? first.map((s) => s.$sym) : [(first as ColSym).$sym];
/**
 * `:agg` raw → typed entries. The §5.8 type-check and the lazy
 * Branch-1 invoke `propagateSchema` on RAW, pre-Zod-validation `data`,
 * so a bare `(out (agg col))` pair written WITHOUT the enclosing `[ ]`
 * reaches `.filter` as a non-array and throws an opaque
 * `parsed.agg is not iterable` TypeError wrapped as "op 'transform'
 * threw". Surface the actionable, retryable `ShapeError` instead.
 * SSOT for both `apply` and `propagateSchema`.
 */
const aggEntriesOf = (rawAgg: unknown, pos: ReturnType<typeof opCallPos>): readonly AggEntryT[] => {
  if (!Array.isArray(rawAgg)) {
    throw new ShapeError(
      ':agg expects a vector of agg entries — wrap it in `[ ]`, e.g. `:agg [(out (agg col))]` (plain form) or `:agg [mean]` (per-cell form)',
      pos,
      ':agg',
    );
  }
  return rawAgg as readonly AggEntryT[];
};

/** Per-cell agg-output column names for a lifted column. */
const perCellAggNames = (liftColumn: string, bareSyms: readonly string[]): string[] =>
  bareSyms.map((s) => `${liftColumn}-${s}`);

/** Inner schema a per-cell time-bucket produces for one lifted column. */
const perCellInnerSchema = (liftColumn: string, bareSyms: readonly string[]): Schema =>
  schemaFromEntries([
    ['bucket', timestampCol],
    ...perCellAggNames(liftColumn, bareSyms).map((n) => [n, unknownCol] as const),
  ]);

export const timeBucket = z
  .object({
    name: z.literal('time-bucket'),
    params: z.tuple([ColsArg]),
    period: z.enum(Periods),
    window: z.tuple([z.string().min(1), z.string().min(1)]).optional(),
    'group-by': z.array(ColSymbol).optional(),
    agg: z.array(AggEntry).min(1),
  })
  .describe(
    'Bucket rows by time at the given `:period` (minute/hour/day/week/month/quarter/year). Plain-timestamp form: `(time-bucket ts :period week :group-by [k] :agg [(out (agg col))])`. Per-cell form over TS-ref column(s): `(time-bucket [colA colB] :period day :window ["<startISO>" "<endISO>"] :agg [min max mean])` — emits a nested-dataset column per input with inner `{bucket, <col>-<agg>…}`. This verb ALWAYS aggregates — for raw, unaggregated per-sample datapoints do not use `time-bucket`; `unnest` the same TS-ref column instead (e.g. `(unnest [priorityOilRateStd] :pick [timestamp value] :as [ts val])`).',
  )
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    childScopes: [aggContextScope],
    example: {
      expr: '(transform ds (time-bucket [oilProd gasProd] :period week :window ["2026-01-01" "2026-04-01"] :agg [min max mean]))',
    },
    apply: (data, dataset: MaterializedDataset, ctx: SpecialVerbCtx): MaterializedDataset => {
      const callPos = opCallPos(ctx.spans, ctx.path);
      const liftColumn = ctx.liftColumn;
      const first = data.params[0] as ColSym | readonly ColSym[];
      const isVectorForm = Array.isArray(first);
      const cols = colsOf(first);
      const period: Period = data.period;
      const entries = aggEntriesOf(data.agg, callPos);
      const bareSyms = entries.filter(isColSym).map((e) => e.$sym);
      const pairEntries = entries.filter((e): e is AggPair => !isColSym(e));
      const groupBy = (data['group-by'] ?? []).map((s) => s.$sym);
      const win = data.window as readonly [string, string] | undefined;

      if (liftColumn !== undefined) {
        // Per-cell form. `dataset` is the inner cell:
        // {timestamp, value, isString, unit}. Bucket its `timestamp`,
        // aggregate its `value` with the bare `:agg` symbols.
        if (groupBy.length > 0) {
          throw new ShapeError(
            ':group-by is not valid on the per-cell (TS-ref) form of time-bucket',
            callPos,
            ':group-by',
          );
        }
        if (pairEntries.length > 0) {
          throw new ShapeError(
            'the `(out (agg col))` :agg pair form is only valid on the plain-timestamp form; use bare agg symbols on the per-cell (TS-ref) form',
            callPos,
            ':agg',
          );
        }
        for (const s of bareSyms) {
          if (!isBareAggSymbol(s)) {
            throw new ShapeError(
              `unknown aggregate \`${s}\`; expected one of ${BARE_AGG_SYMBOLS.join(', ')}`,
              callPos,
              ':agg',
            );
          }
        }
        const pairs = bareSyms.map((s) => compileBareAgg(s, liftColumn));
        const startD = win ? toDateValue(win[0]) : null;
        const endD = win ? toDateValue(win[1]) : null;

        const bucketKey = '__bucketed__';
        const bucketed = materializedToRows(dataset)
          .filter((row) => {
            if (win === undefined) {
              return true;
            }
            const t = toDateValue(row.timestamp);
            if (t === null) {
              return false;
            }
            if (startD !== null && t < startD) {
              return false;
            }
            return !(endD !== null && t >= endD);
          })
          .flatMap((row): Record<string, unknown>[] => {
            const b = bucketTimestamp(row.timestamp, period);
            return b === null ? [] : [{ ...row, [bucketKey]: b }];
          });

        const byMs = new Map<number, readonly Record<string, unknown>[]>();
        for (const g of groupRowsBy(bucketed, [bucketKey])) {
          byMs.set((g.representativeRow[bucketKey] as Date).getTime(), g.rows);
        }
        // With `:window`, emit the deterministic grid (gap buckets
        // null-filled) so every axis aligns by construction; without
        // it, the observed buckets, ascending.
        const orderedKeys: readonly Date[] =
          win === undefined
            ? Array.from(byMs.keys())
                .sort((a, b) => a - b)
                .map((ms) => new Date(ms))
            : periodGrid(win[0], win[1], period);

        const aggNames = perCellAggNames(liftColumn, bareSyms);
        const outCols: Record<string, Cell[]> = { bucket: [] };
        for (const n of aggNames) {
          outCols[n] = [];
        }
        for (const k of orderedKeys) {
          const rows = byMs.get(k.getTime()) ?? [];
          (outCols.bucket as Cell[]).push(k as Cell);
          pairs.forEach((p, i) => {
            (outCols[aggNames[i] as string] as Cell[]).push((rows.length > 0 ? p.reduce(rows) : null) as Cell);
          });
        }
        return new MaterializedDataset(outCols, perCellInnerSchema(liftColumn, bareSyms));
      }

      // Plain-timestamp (row-level) form.
      if (isVectorForm) {
        throw new ShapeError(
          'the `[col …]` vector positional of time-bucket is only valid on TS-ref (nested-dataset) columns; pass a single timestamp column for row-level bucketing',
          callPos,
          '#0',
        );
      }
      if (bareSyms.length > 0) {
        throw new ShapeError(
          'bare-symbol :agg is only valid on the per-cell (TS-ref) form; use `(out (agg col))` pairs for row-level bucketing',
          callPos,
          ':agg',
        );
      }
      const tsCol = cols[0] as string;
      const aggPairs = pairEntries.map((pair, i) => compileAggPair(pair, paramPath(paramPath(ctx.path, 0), i)));

      if (!dataset.schema.has(tsCol)) {
        throw new TypeMismatchError(
          `time-bucket column \`${tsCol}\` to exist on the threaded dataset`,
          'no such column',
          'time-bucket',
          callPos,
        );
      }
      for (const k of groupBy) {
        if (!dataset.schema.has(k)) {
          throw new TypeMismatchError(
            `:group-by column \`${k}\` to exist on the threaded dataset`,
            'no such column',
            'time-bucket',
            callPos,
          );
        }
      }

      const bucketKey = '__bucketed__';
      const rowsWithBucket = materializedToRows(dataset).flatMap((row): Record<string, unknown>[] => {
        const bucket = bucketTimestamp(row[tsCol], period);
        return bucket === null ? [] : [{ ...row, [bucketKey]: bucket }];
      });
      const groups = groupRowsBy(rowsWithBucket, [bucketKey, ...groupBy]);

      const outRows = groups.map(({ representativeRow, rows }) => {
        const out: Record<string, Cell> = {};
        out[tsCol] = representativeRow[bucketKey] as Cell;
        for (const k of groupBy) {
          out[k] = representativeRow[k] as Cell;
        }
        for (const { outName, reduce } of aggPairs) {
          out[outName] = reduce(rows) as Cell;
        }
        return out;
      });

      const outColOrder = [tsCol, ...groupBy, ...aggPairs.map((p) => p.outName)];
      const outputCols: Record<string, Cell[]> = {};
      for (const k of outColOrder) {
        outputCols[k] = outRows.map((r) => (r[k] ?? null) as Cell);
      }
      return new MaterializedDataset(
        outputCols,
        propagateTimeBucketSchema(
          dataset.schema,
          tsCol,
          groupBy,
          aggPairs.map((p) => p.outName),
        ),
      );
    },
    propagateSchema: (data, ctx: PropagateSchemaCtx): Schema => {
      const parsed = data as z.infer<typeof timeBucket>;
      const first = parsed.params[0] as ColSym | readonly ColSym[];
      const cols = colsOf(first);
      const entries = aggEntriesOf(parsed.agg, NO_POS);
      const bareSyms = entries.filter(isColSym).map((e) => e.$sym);
      const pairOutNames = entries.filter((e): e is AggPair => !isColSym(e)).map((p) => p.name);
      const groupBy = (parsed['group-by'] ?? []).map((s) => s.$sym);

      // Per-cell: `inputSchema` is the cell's inner TS-ref schema.
      if (ctx.liftColumn !== undefined) {
        return perCellInnerSchema(ctx.liftColumn, bareSyms);
      }
      // Lazy / §5.8 type-check OUTER rewrite: any first-positional
      // column that is `nested-dataset` on the input schema becomes a
      // nested-dataset of `{bucket, <col>-<agg>…}` — agreeing with
      // the runtime per-cell lift's outer rewrite.
      const nestedCols = cols.filter((c) => ctx.inputSchema.get(c)?.kind === 'nested-dataset');
      if (nestedCols.length > 0) {
        const nestedSet = new Set(nestedCols);
        return schemaFromEntries(
          Array.from(ctx.inputSchema.entries()).map(
            ([k, t]) =>
              (nestedSet.has(k) ? [k, nestedDatasetCol(perCellInnerSchema(k, bareSyms))] : [k, t]) as readonly [
                string,
                ColumnType,
              ],
          ),
        );
      }
      // Plain timestamp form.
      return propagateTimeBucketSchema(ctx.inputSchema, cols[0] as string, groupBy, pairOutNames);
    },
    // Report every first-positional column (single or vector) so the
    // dispatch wrapper lifts per-cell on each TS-ref axis (D11.1).
    // Plain-timestamp columns are reported too but, being non-nested,
    // never trigger a lift. Pair `:agg` inner symbols + `:group-by`
    // are reported for unknown-ref diagnostics.
    referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
      const parsed = data as z.infer<typeof timeBucket>;
      const first = parsed.params[0] as ColSym | readonly ColSym[];
      const refs: ColumnRefInArg[] = colsOf(first).map((name) => ({ name, path: paramPath(basePath, 0) }));
      // Vector positional ⇒ per-cell form: `:group-by` and pair-`:agg`
      // are invalid here (rejected in `apply`) and their symbols refer
      // to INNER cell columns, not the OUTER schema the §5.8 ref-check
      // validates against. Reporting them would surface a misleading
      // `UnknownColumnRefError` instead of the precise `ShapeError`.
      // Only the lift-axis columns are outer refs.
      if (Array.isArray(first)) {
        return refs;
      }
      // Single symbol ⇒ plain-timestamp form: `:group-by` keys and
      // pair-`:agg` inner symbols ARE outer columns — report them so
      // unknown-ref diagnostics still fire.
      for (const k of parsed['group-by'] ?? []) {
        refs.push({ name: k.$sym, path: basePath });
      }
      for (const e of aggEntriesOf(parsed.agg, NO_POS)) {
        if (isColSym(e)) {
          continue; // bare agg symbol — not a column ref
        }
        for (const arg of e.params[0]?.params ?? []) {
          const shape = astShape(arg);
          if (shape.kind === 'symbolRef') {
            refs.push({ name: shape.sym, path: basePath });
          }
        }
      }
      return refs;
    },
  });

const propagateTimeBucketSchema = (
  inputSchema: Schema,
  tsCol: string,
  groupBy: readonly string[],
  aggOutNames: readonly string[],
): Schema => {
  const entries: [string, ColumnType][] = [[tsCol, timestampCol]];
  for (const k of groupBy) {
    const t = inputSchema.get(k);
    if (t !== undefined) {
      entries.push([k, t]);
    }
  }
  for (const name of aggOutNames) {
    entries.push([name, unknownCol]);
  }
  return schemaFromEntries(entries);
};
